const GATEWAY = process.env.REACT_APP_GATEWAY_URL || 'http://localhost:4000'

/**
 * Store credits — rewards and promotions the atelier grants. Not a topup wallet:
 * there is no deposit path anywhere in this API, and the balance is always the
 * server's derivation from its own ledger. Nothing here is ever sent BACK to the
 * server as an amount to trust — see applyCredits.
 */

export type WalletTxnType =
  | 'credit_added'
  | 'credit_used'
  | 'credit_expired'
  | 'credit_refunded'

export interface WalletTransaction {
  id:         string
  type:       WalletTxnType
  /** Positive when credited, negative when spent or expired. */
  amount:     number
  reference:  string | null
  order_id:   string | null
  expires_at: string | null
  created_at: string
}

export interface WalletBalance {
  balance:         number
  lifetime_earned: number
  lifetime_used:   number
  /** Most recent handful, for the profile card. */
  transactions:    WalletTransaction[]
}

export interface WalletHistoryPage {
  transactions: WalletTransaction[]
  pagination:   { total: number; page: number; limit: number; pages: number }
  balance:      number
}

/**
 * The customer's wallet.
 *
 * Returns null instead of throwing when anything goes wrong, and that is the
 * contract the profile page depends on: the credits card is an extra, and a
 * wallet outage must hide one section rather than blank a page the customer
 * opened to check their addresses.
 */
export async function getWalletBalance(token: string): Promise<WalletBalance | null> {
  try {
    const res = await fetch(`${GATEWAY}/api/wallet/balance`, {
      headers: { Authorization: `Bearer ${token}` },
      credentials: 'include',
    })
    if (!res.ok) return null
    const data = await res.json()
    if (typeof data?.balance !== 'number') return null
    return {
      balance:         data.balance,
      lifetime_earned: data.lifetime_earned ?? 0,
      lifetime_used:   data.lifetime_used ?? 0,
      transactions:    Array.isArray(data.transactions) ? data.transactions : [],
    }
  } catch {
    return null
  }
}

/** Paginated history behind the "View all" link. Throws — the modal shows it. */
export async function getWalletTransactions(
  token: string,
  page = 1,
  limit = 20,
): Promise<WalletHistoryPage> {
  const res = await fetch(`${GATEWAY}/api/wallet/transactions?page=${page}&limit=${limit}`, {
    headers: { Authorization: `Bearer ${token}` },
    credentials: 'include',
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data?.error || 'Could not load your credit history')
  return data as WalletHistoryPage
}

export interface CreditPreview {
  valid:          boolean
  /** What will actually come off — capped at the balance and at the order value. */
  credits_to_use: number
  /** What Cashfree will be asked for once this credit is applied. */
  new_total:      number
  is_free_order:  boolean
  balance:        number
}

/**
 * Ask the server what a credit would do to this order, before committing.
 *
 * Advisory only. Checkout recomputes every figure from a freshly read balance,
 * so the worst a stale preview can cause is a number moving between the summary
 * and the order — never an overspend.
 */
export async function applyCredits(
  token: string,
  creditsToUse: number,
  couponCode?: string | null,
): Promise<CreditPreview> {
  const res = await fetch(`${GATEWAY}/api/checkout/apply-credits`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    credentials: 'include',
    body: JSON.stringify({ credits_to_use: creditsToUse, coupon_code: couponCode || null }),
  })
  const data = await res.json()
  if (!res.ok) throw new Error(data?.error || 'Could not apply your credits')
  return data as CreditPreview
}

// ── Client-side preview ───────────────────────────────────────────────────────

/**
 * Cashfree's floor. Mirrors MIN_GATEWAY_CHARGE in the ecommerce service's
 * lib/wallet.ts, for the same reason utils/guestCart mirrors the GST divisor:
 * the checkout summary has to show the figure the order will actually use, and
 * asking the server on every keystroke would make the toggle feel broken.
 *
 * The server is still the authority — POST /api/checkout re-derives all of this
 * against a freshly read balance and ignores whatever the page believed. Keep
 * the two in sync; if they ever drift, the server's answer is the correct one.
 */
export const MIN_GATEWAY_CHARGE = 1

const round2 = (n: number) => Math.round(n * 100) / 100

export interface CreditSplit {
  /** Credit that comes off this order. */
  credits_applied: number
  /** What the customer pays the gateway. */
  payable:         number
  is_free_order:   boolean
}

/**
 * How much of a balance this order can absorb — the same three rules the server
 * applies, in the same order: never more than held, never more than owed, and
 * never leave the gateway less than its minimum.
 */
export function previewCredits(balance: number, dueAfterCoupon: number): CreditSplit {
  const due = round2(Math.max(0, dueAfterCoupon))
  let applied = round2(Math.min(Math.max(0, balance), due))

  if (applied <= 0) return { credits_applied: 0, payable: due, is_free_order: due === 0 }
  if (applied >= due) return { credits_applied: due, payable: 0, is_free_order: true }

  if (round2(due - applied) < MIN_GATEWAY_CHARGE) {
    applied = round2(due - MIN_GATEWAY_CHARGE)
    if (applied <= 0) return { credits_applied: 0, payable: due, is_free_order: false }
  }

  return { credits_applied: applied, payable: round2(due - applied), is_free_order: false }
}
